import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Eye, EyeOff, Pencil, Plus, Trash2 } from "lucide-react";
import {
  CRM_LEAD_CHANNEL_FIELD_GROUP_IDS,
  CRM_LEAD_CHANNEL_FIELD_GROUP_LABELS,
  CRM_LEAD_FIELD_SECTION_IDS,
  CRM_LEAD_FIELD_SECTION_LABELS,
  crmLeadFieldDetailPath,
  crmLeadFieldIsHidden,
  crmLeadFieldsForSectionList,
  standardIndustrySelectOptions,
  sectionHasChannelFieldGroups,
  type CrmLeadFieldDefinition,
  type CrmLeadFieldSelectOption,
  type CrmLeadFieldSectionId,
  type CrmLeadModuleFieldsDocument,
} from "@/lib/crmLeadModuleFields";

const fieldClass =
  "w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20";

type CrmLeadModuleFieldsPanelProps = {
  sections: CrmLeadModuleFieldsDocument["sections"];
  onChange: (next: CrmLeadModuleFieldsDocument["sections"]) => void;
  canManage: boolean;
  disabled?: boolean;
};

function kindLabel(kind: CrmLeadFieldDefinition["kind"]): string {
  if (kind === "text") return "Text";
  if (kind === "select") return "Select";
  return "Field";
}

function newFieldId(section: CrmLeadFieldSectionId): string {
  return `${section}_custom_${Date.now().toString(36)}`;
}

export function CrmLeadModuleFieldsPanel({
  sections,
  onChange,
  canManage,
  disabled,
}: CrmLeadModuleFieldsPanelProps) {
  const [sectionId, setSectionId] = useState<CrmLeadFieldSectionId>("lead_status");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<CrmLeadFieldDefinition | null>(null);

  const allFields = sections[sectionId] ?? [];
  const listFields = useMemo(
    () => crmLeadFieldsForSectionList(sectionId, allFields),
    [sectionId, allFields],
  );
  const showChannelGroups = sectionHasChannelFieldGroups(sectionId);

  const startCreate = () => {
    const base: CrmLeadFieldDefinition = {
      id: newFieldId(sectionId),
      label: "Custom field",
      kind: sectionId === "lead_status" ? "select" : "text",
      isStandard: false,
    };
    if (base.kind === "select") {
      base.selectOptions = [{ value: "option_a", label: "Option A" }];
    }
    setDraft(base);
    setEditingId(base.id);
  };

  const startEdit = (field: CrmLeadFieldDefinition) => {
    setDraft({ ...field });
    setEditingId(field.id);
  };

  const cancelEdit = () => {
    setDraft(null);
    setEditingId(null);
    setNewOptionValue("");
    setNewOptionLabel("");
    setNewOptionParent("");
  };

  const commitEdit = () => {
    if (!draft) return;
    const label = draft.label.trim();
    const id = draft.id.trim();
    if (!label || !id) return;
    const nextField = { ...draft, label, id };
    const exists = allFields.some((f) => f.id === nextField.id);
    const nextList = exists
      ? allFields.map((f) => (f.id === editingId ? nextField : f))
      : [...allFields, nextField];
    onChange({ ...sections, [sectionId]: nextList });
    cancelEdit();
  };

  const removeField = (id: string) => {
    onChange({
      ...sections,
      [sectionId]: allFields.filter((f) => f.id !== id),
    });
    if (editingId === id) cancelEdit();
  };

  const toggleFieldHidden = (id: string) => {
    onChange({
      ...sections,
      [sectionId]: allFields.map((f) =>
        f.id === id ? { ...f, hidden: f.hidden ? undefined : true } : f,
      ),
    });
    if (editingId === id && draft) {
      setDraft({ ...draft, hidden: draft.hidden ? undefined : true });
    }
  };

  const setSelectOptionHidden = (optionValue: string, hidden: boolean) => {
    if (!draft?.selectOptions) return;
    setDraft({
      ...draft,
      selectOptions: draft.selectOptions.map((o) =>
        o.value === optionValue ? { ...o, hidden: hidden ? true : undefined } : o,
      ),
    });
  };

  const industryParentOptions = useMemo(() => {
    const industryField = (sections.account_lead ?? []).find((f) => f.id === "industry");
    if (industryField?.selectOptions?.length) return industryField.selectOptions;
    return standardIndustrySelectOptions();
  }, [sections.account_lead]);

  const isIndustrySegmentField = draft?.id === "industry_segment";

  const upsertSelectOption = (option: CrmLeadFieldSelectOption) => {
    if (!draft) return;
    const value = option.value.trim();
    const label = option.label.trim() || value;
    if (!value) return;
    const nextOption: CrmLeadFieldSelectOption = { value, label };
    if (option.hidden) nextOption.hidden = true;
    if (option.parentOptionValue?.trim()) {
      nextOption.parentOptionValue = option.parentOptionValue.trim();
    }
    const list = draft.selectOptions ?? [];
    const exists = list.some((o) => o.value === value);
    setDraft({
      ...draft,
      selectOptions: exists
        ? list.map((o) => (o.value === value ? { ...o, ...nextOption } : o))
        : [...list, nextOption],
    });
  };

  const removeSelectOption = (optionValue: string) => {
    if (!draft?.selectOptions) return;
    setDraft({
      ...draft,
      selectOptions: draft.selectOptions.filter((o) => o.value !== optionValue),
    });
  };

  const [newOptionValue, setNewOptionValue] = useState("");
  const [newOptionLabel, setNewOptionLabel] = useState("");
  const [newOptionParent, setNewOptionParent] = useState("");

  const addDraftSelectOption = () => {
    upsertSelectOption({
      value: newOptionValue,
      label: newOptionLabel,
      parentOptionValue: isIndustrySegmentField ? newOptionParent : undefined,
    });
    setNewOptionValue("");
    setNewOptionLabel("");
    setNewOptionParent("");
  };

  const editing = draft !== null;
  const editingExisting =
    editingId !== null && allFields.some((f) => f.id === editingId);

  const sectionHelp = useMemo(() => {
    if (sectionId === "lead_status") {
      return "Standard Lead status fields used on New Lead (Source, Stage, Status).";
    }
    if (sectionId === "account_lead") {
      return "Account Lead section fields. Open Email, Phone, or Social to manage channel types and defaults.";
    }
    return "Contact Lead section fields. Open Email, Phone, or Social to manage channel types and defaults.";
  }, [sectionId]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Section
          <select
            value={sectionId}
            disabled={disabled}
            onChange={(e) => {
              setSectionId(e.target.value as CrmLeadFieldSectionId);
              cancelEdit();
            }}
            className={`${fieldClass} mt-1 min-w-[12rem]`}
          >
            {CRM_LEAD_FIELD_SECTION_IDS.map((id) => (
              <option key={id} value={id}>
                {CRM_LEAD_FIELD_SECTION_LABELS[id]}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-400">{sectionHelp}</p>

      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-white/10">
        <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-white/10">
          <thead className="bg-slate-50 dark:bg-white/5">
            <tr>
              <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">Label</th>
              <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">Kind</th>
              <th className="px-3 py-2 text-left font-semibold text-slate-700 dark:text-slate-200">Required</th>
              <th className="px-3 py-2 text-right font-semibold text-slate-700 dark:text-slate-200">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-white/5">
            {listFields.map((field) => (
              <tr key={field.id} className={crmLeadFieldIsHidden(field) ? "opacity-70" : undefined}>
                <td className="px-3 py-2 text-slate-900 dark:text-white">
                  {field.label}
                  {field.isStandard ? (
                    <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400">Standard</span>
                  ) : null}
                  {crmLeadFieldIsHidden(field) ? (
                    <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 dark:bg-white/10 dark:text-slate-300">
                      Hidden
                    </span>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{kindLabel(field.kind)}</td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{field.required ? "Yes" : "No"}</td>
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-1">
                    {canManage ? (
                      <>
                        <button
                          type="button"
                          disabled={disabled}
                          aria-label={crmLeadFieldIsHidden(field) ? `Unhide ${field.label}` : `Hide ${field.label}`}
                          title={crmLeadFieldIsHidden(field) ? "Unhide on New Lead" : "Hide on New Lead"}
                          onClick={() => toggleFieldHidden(field.id)}
                          className="rounded-md border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50 dark:border-white/20 dark:text-slate-300"
                        >
                          {crmLeadFieldIsHidden(field) ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          type="button"
                          disabled={disabled}
                          aria-label={`Edit ${field.label}`}
                          onClick={() => startEdit(field)}
                          className="rounded-md border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50 dark:border-white/20 dark:text-slate-300"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          disabled={disabled || field.isStandard}
                          aria-label={`Delete ${field.label}`}
                          onClick={() => removeField(field.id)}
                          className="rounded-md border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50 disabled:opacity-40 dark:border-white/20 dark:text-slate-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
            {showChannelGroups
              ? CRM_LEAD_CHANNEL_FIELD_GROUP_IDS.map((groupId) => (
                  <tr key={groupId} className="bg-slate-50/50 dark:bg-white/[0.02]">
                    <td className="px-3 py-2">
                      <Link
                        to={crmLeadFieldDetailPath(sectionId, groupId)}
                        className="inline-flex items-center gap-1 font-medium text-indigo-700 hover:text-indigo-600 dark:text-indigo-300"
                      >
                        {CRM_LEAD_CHANNEL_FIELD_GROUP_LABELS[groupId]}
                        <ChevronRight className="h-4 w-4 opacity-70" aria-hidden />
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-slate-600 dark:text-slate-300">Channel field</td>
                    <td className="px-3 py-2 text-slate-400">n/a</td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to={crmLeadFieldDetailPath(sectionId, groupId)}
                        className="text-xs font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-300"
                      >
                        Manage
                      </Link>
                    </td>
                  </tr>
                ))
              : null}
            {!listFields.length && !showChannelGroups ? (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-slate-500">
                  No fields in this section yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {canManage ? (
        <button
          type="button"
          disabled={disabled || Boolean(editing)}
          onClick={startCreate}
          className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-800 hover:bg-indigo-100 disabled:opacity-50 dark:border-indigo-500/30 dark:bg-indigo-950/40 dark:text-indigo-200"
        >
          <Plus className="h-3.5 w-3.5" />
          Create field
        </button>
      ) : null}

      {editing && draft ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
            {editingExisting ? "Update field" : "Create field"}
          </h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
              Label
              <input
                value={draft.label}
                disabled={disabled}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                className={`${fieldClass} mt-1`}
              />
            </label>
            <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
              Field id
              <input
                value={draft.id}
                disabled={disabled || draft.isStandard || editingExisting}
                readOnly={editingExisting}
                onChange={(e) => setDraft({ ...draft, id: e.target.value })}
                className={`${fieldClass} mt-1${editingExisting ? " cursor-not-allowed opacity-80" : ""}`}
              />
              {editingExisting ? (
                <span className="mt-1 block text-[11px] font-normal text-slate-500 dark:text-slate-400">
                  Field id cannot be changed after the field is created.
                </span>
              ) : null}
            </label>
            {sectionId === "lead_status" && !draft.isStandard ? (
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
                Kind
                <select
                  value={draft.kind}
                  disabled={disabled}
                  onChange={(e) =>
                    setDraft({ ...draft, kind: e.target.value as CrmLeadFieldDefinition["kind"] })
                  }
                  className={`${fieldClass} mt-1`}
                >
                  <option value="text">Text</option>
                  <option value="select">Select</option>
                </select>
              </label>
            ) : null}
            <label className="flex items-center gap-2 pt-6 text-xs text-slate-600 dark:text-slate-300">
              <input
                type="checkbox"
                checked={Boolean(draft.required)}
                disabled={disabled}
                onChange={(e) => setDraft({ ...draft, required: e.target.checked })}
              />
              Required on New Lead
            </label>
            <label className="flex items-center gap-2 pt-6 text-xs text-slate-600 dark:text-slate-300">
              <input
                type="checkbox"
                checked={Boolean(draft.hidden)}
                disabled={disabled}
                onChange={(e) => setDraft({ ...draft, hidden: e.target.checked ? true : undefined })}
              />
              Hidden on New Lead
            </label>
          </div>
          {draft.kind === "select" && draft.selectOptions?.length ? (
            <div className="mt-4">
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">Select options</p>
              <ul className="mt-2 space-y-1">
                {draft.selectOptions.map((opt) => (
                  <li
                    key={opt.value}
                    className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-200 px-2 py-1.5 text-xs dark:border-white/10"
                  >
                    <span className="text-slate-800 dark:text-slate-100">
                      {opt.label}{" "}
                      <span className="text-slate-400">({opt.value})</span>
                      {opt.parentOptionValue ? (
                        <span className="ml-2 text-slate-500">
                          ·{" "}
                          {industryParentOptions.find((p) => p.value === opt.parentOptionValue)?.label ??
                            opt.parentOptionValue}
                        </span>
                      ) : null}
                      {opt.hidden ? (
                        <span className="ml-2 rounded bg-slate-200 px-1 py-0.5 text-[10px] font-semibold uppercase dark:bg-white/10">
                          Hidden
                        </span>
                      ) : null}
                    </span>
                    <span className="flex flex-wrap gap-1">
                      {!draft.isStandard ? (
                        <button
                          type="button"
                          disabled={disabled}
                          onClick={() => removeSelectOption(opt.value)}
                          className="rounded border border-rose-200 px-2 py-0.5 font-semibold text-rose-700 hover:bg-rose-50 dark:border-rose-500/30 dark:text-rose-300"
                        >
                          Remove
                        </button>
                      ) : null}
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => setSelectOptionHidden(opt.value, !opt.hidden)}
                        className="rounded border border-slate-300 px-2 py-0.5 font-semibold text-slate-600 hover:bg-slate-50 dark:border-white/20 dark:text-slate-300"
                      >
                        {opt.hidden ? "Unhide" : "Hide"}
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
              {canManage ? (
                <div className="mt-3 grid gap-2 rounded border border-dashed border-slate-300 p-3 dark:border-white/15">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Add option
                  </p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <input
                      value={newOptionValue}
                      disabled={disabled}
                      onChange={(e) => setNewOptionValue(e.target.value)}
                      placeholder="Value (slug)"
                      className={fieldClass}
                    />
                    <input
                      value={newOptionLabel}
                      disabled={disabled}
                      onChange={(e) => setNewOptionLabel(e.target.value)}
                      placeholder="Label"
                      className={fieldClass}
                    />
                    {isIndustrySegmentField ? (
                      <label className="text-xs font-medium text-slate-600 dark:text-slate-300 sm:col-span-2">
                        Parent industry
                        <select
                          value={newOptionParent}
                          disabled={disabled}
                          onChange={(e) => setNewOptionParent(e.target.value)}
                          className={`${fieldClass} mt-1`}
                        >
                          <option value="">Select industry…</option>
                          {industryParentOptions
                            .filter((p) => !p.hidden)
                            .map((p) => (
                              <option key={p.value} value={p.value}>
                                {p.label}
                              </option>
                            ))}
                        </select>
                      </label>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    disabled={
                      disabled ||
                      !newOptionValue.trim() ||
                      !newOptionLabel.trim() ||
                      (isIndustrySegmentField && !newOptionParent.trim())
                    }
                    onClick={addDraftSelectOption}
                    className="w-fit rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-800 hover:bg-indigo-100 disabled:opacity-50 dark:border-indigo-500/30 dark:bg-indigo-950/40 dark:text-indigo-200"
                  >
                    Add option
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={disabled}
              onClick={commitEdit}
              className="rounded-md bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-500"
            >
              Save field
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={cancelEdit}
              className="rounded-md border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 dark:border-white/20 dark:text-slate-200"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {!canManage ? (
        <p className="text-xs text-slate-500">Only account owners and admins can change field definitions.</p>
      ) : null}
    </div>
  );
}
